import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Typography,
  Tab,
  Tabs,
  Divider,
  Container,
  Stack,
  Box,
  TextField,
  Checkbox,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  PreviewTitleBox,
  StyledNotes,
  StyledNoticeStack,
  StyledLink,
  StyledTypeCell,
} from './ActionPlan.styles';
import { useSelectedItemsValidation } from '../../hooks';
import {
  KpiBadge,
  ControlBar,
  IControlButton,
  MainContainer,
  FormGrid,
  FormItemLabel,
  IFieldValues,
  TableComponent,
  SendFormModal,
  useNotification,
  Loader,
} from '../../components';
import {
  ActionPlanGroupsTitle,
  ActionPlanGroupsTitleDescription,
  blocksOrder,
  InitiativeColumns,
  CalculatedKpiColumns,
  RawKpiColumns,
  CompletedInitiativeColumns,
} from './types';
import URLs from '../URLs';
import { useInitiativesStore, useSalonStore } from '../../store';
import { Initiative } from '../../types/Initiative';
import { InitiativeType } from '../../types/Initiative';
import { useTranslation } from 'react-i18next';
import { convertToSnakeCase, LocalizedColumns } from '../../utils';
import {
  ActionGuestPlan,
  ActionKPI,
  ActionPlanCount,
  BenchmarkItemValues,
  KpiItemValues,
  KpiStatus,
  KpiTitle,
  SalonActionPlan,
  SalonKpiData,
  SalonKpiPlannedData,
} from '../../types';
import { KpiValues, SalonKpiValues } from '../KpiResults/types';
import { normalizeYearQuarter } from '../../utils/normalizeYearQuarter';
import { PLAN_TYPE, SELECT_INPUT_KPIS } from './constants';
import { DueDatePicker } from './components/DueDatePicker';
import { LOCAL_RESOURCE_ID_PREFIX, useCustomResources } from './hooks/useCustomResources';
import { useInitiativesStatus } from './hooks/useInitiativesStatus';

const QUARTER_SUFFIX = 'q';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <MainContainer>{children}</MainContainer>}
    </div>
  );
};

export const ActionPlan = () => {
  const { t } = useTranslation();
  const NOT_PROVIDED = t('notProvided');

  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const { showMessage } = useNotification();

  const [plannedKpiValues, setPlannedKpiValues] = useState<null | SalonKpiPlannedData['kpi']>(null);
  const initiativesStatusApi = useInitiativesStatus();

  const {
    isLoading: isSalonDataLoading,
    salon,
    actionPlan,
    getActionPlan,
    salonKpiPlannedData,
    fetchSalonKpiPlannedData,
    updateSalonKpiPlannedData,
    fetchSalon,
    updateActionPlan,
  } = useSalonStore();
  const { isLoading, initiatives, fetchInitiatives, modifyInitiative } = useInitiativesStore();

  // Calculate initial selected items synchronously when we have the data
  const calculateInitialSelectedItems = useMemo(() => {
    if (!actionPlan?.initiatives || !initiatives.length) {
      return {};
    }

    const newInitialSelectedItems: Record<string, Array<string>> = {};

    actionPlan.initiatives.forEach((actionPlanInitiative) => {
      // Auto-select any initiative that was previously selected (exists in action plan)
      // This preserves checkbox selections even if no content was added yet
      const initiative = initiatives.find((i) => i.id === actionPlanInitiative.id);

      if (initiative?.actionPlanType) {
        // Map actionPlanType to tab names
        let tabName = '';
        const actionPlanType = initiative.actionPlanType as string;

        if (
          actionPlanType === PLAN_TYPE.guestPlan.field ||
          actionPlanType === PLAN_TYPE.guestPlan.title
        ) {
          tabName = PLAN_TYPE.guestPlan.id;
        } else if (
          actionPlanType === PLAN_TYPE.teamPlan.field ||
          actionPlanType === PLAN_TYPE.teamPlan.title
        ) {
          tabName = PLAN_TYPE.teamPlan.id;
        } else if (
          actionPlanType === PLAN_TYPE.higherTicketsPlan.field ||
          actionPlanType === PLAN_TYPE.higherTicketsPlan.title
        ) {
          tabName = PLAN_TYPE.higherTicketsPlan.id;
        }

        if (tabName) {
          // Initialize tab array if it doesn't exist
          if (!newInitialSelectedItems[tabName]) {
            newInitialSelectedItems[tabName] = [];
          }

          // Add to selected items if not already included
          if (!newInitialSelectedItems[tabName].includes(actionPlanInitiative.id)) {
            newInitialSelectedItems[tabName].push(actionPlanInitiative.id);
            // Debug log removed for production
          }
        }
      }
    });

    actionPlan.customResources.forEach((actionPlanCustomResource) => {
      // Auto-select any custom resource that was previously selected (exists in action plan)
      // This preserves checkbox selections even if no content was added yet
      let tabName = '';
      const actionPlanType = actionPlanCustomResource.actionPlanType as string;

      if (
        actionPlanType === PLAN_TYPE.guestPlan.field ||
        actionPlanType === PLAN_TYPE.guestPlan.title
      ) {
        tabName = PLAN_TYPE.guestPlan.id;
      } else if (
        actionPlanType === PLAN_TYPE.teamPlan.field ||
        actionPlanType === PLAN_TYPE.teamPlan.title
      ) {
        tabName = PLAN_TYPE.teamPlan.id;
      } else if (
        actionPlanType === PLAN_TYPE.higherTicketsPlan.field ||
        actionPlanType === PLAN_TYPE.higherTicketsPlan.title
      ) {
        tabName = PLAN_TYPE.higherTicketsPlan.id;
      }

      if (tabName) {
        // Initialize tab array if it doesn't exist
        if (!newInitialSelectedItems[tabName]) {
          newInitialSelectedItems[tabName] = [];
        }

        // Add to selected items if not already included
        if (!newInitialSelectedItems[tabName].includes(actionPlanCustomResource.crUuid)) {
          newInitialSelectedItems[tabName].push(actionPlanCustomResource.crUuid);
        }
      }
    });

    return newInitialSelectedItems;
  }, [
    actionPlan?.initiatives,
    actionPlan?.customResources,
    initiatives,
    PLAN_TYPE.guestPlan.title,
    PLAN_TYPE.teamPlan.title,
    PLAN_TYPE.higherTicketsPlan.title,
  ]);

  const { selectedItems, isMinSelected, isMaxSelected, onCheckboxClick } =
    useSelectedItemsValidation(calculateInitialSelectedItems);
  const customResourcesApi = useCustomResources();
  const [editableNoteId, setEditableNoteId] = useState<string>('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const navigate = useNavigate();
  const { salonId, planId, completed } = useParams();
  const [fieldsValues, setFieldsValues] = useState<IFieldValues>({});
  // Local state to preserve UI values even when items are deselected
  const [localInitiativeValues, setLocalInitiativeValues] = useState<
    Record<string, { note?: string; dueDate?: string }>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const isCompleted = completed === 'completed';

  const backButtonHandler = async () => {
    if (isPreviewMode) {
      isCompleted ? navigate(generatePath(URLs.Salon, { salonId })) : setIsPreviewMode(false);
    } else {
      // Save before navigating
      try {
        if (
          initiatives.length > 0 &&
          Object.keys(selectedItems).some((key) => selectedItems[key].length > 0)
        ) {
          await updatePlan();
        }
      } catch (error) {
        console.error('Error saving draft:', error);
      }

      currentTab !== 0 ? setCurrentTab(currentTab - 1) : navigate(-1);
    }
  };
  useEffect(() => scrollTo(0, 0), [isPreviewMode]);

  useEffect(() => {
    if (actionPlan) {
      customResourcesApi.setResources({ resources: actionPlan.customResources || [] });
      initiativesStatusApi.setData(actionPlan);
    }
  }, [actionPlan]);

  useEffect(() => {
    isCompleted && setIsPreviewMode(true);
  }, []);

  useEffect(() => {
    if (salon) fetchInitiatives(Object.keys(salon?.mCountry)?.[0], showMessage, navigate);
  }, [salon]);

  useEffect(() => {
    if (salonId && !salon) {
      fetchSalon(salonId, showMessage, navigate);
    }
  }, [salon, salonId]);

  useEffect(() => {
    if (salonId && actionPlan) {
      fetchSalonKpiPlannedData({
        salonId,
        year: actionPlan?.year,
        quarter: actionPlan?.quarter,
        showMessage: () => {},
        navigate,
      });
    }
  }, [salonId, actionPlan]);

  useEffect(() => {
    if (salonKpiPlannedData?.kpi) {
      setPlannedKpiValues(salonKpiPlannedData.kpi);
    } else if (actionPlan) {
      const currentTabId = planTabs[currentTab] as keyof SalonActionPlan;
      const actionPlanTab = actionPlan[currentTabId] as
        | SalonActionPlan[typeof PLAN_TYPE.guestPlan.id]
        | SalonActionPlan[typeof PLAN_TYPE.teamPlan.id]
        | SalonActionPlan[typeof PLAN_TYPE.higherTicketsPlan.id];
      const actionPlanCurrentTabKpis = actionPlanTab?.kpi;
      const emptyPlannedKpiObject = Object.entries(actionPlanCurrentTabKpis).reduce(
        (acc, [key, obj]) => {
          if (typeof obj.kpi === 'object' && obj.kpi !== null) {
            const { status, prefix, suffix, planType } = obj.kpi;
            acc[key] = {
              value: undefined,
              status,
              prefix,
              suffix,
              planType,
            };
          } else {
            acc[key] = {
              value: undefined,
              status: null,
              prefix: null,
              suffix: null,
              planType: null,
            };
          }

          return acc;
        },
        {} as SalonKpiData['kpi'],
      );

      setPlannedKpiValues(emptyPlannedKpiObject);
    } else {
      setPlannedKpiValues({});
    }
  }, [salonKpiPlannedData, actionPlan]);

  useEffect(() => {
    if (planId) {
      getActionPlan(planId, showMessage, navigate);
    }
  }, [planId]);

  useEffect(() => {
    // When action plan is loaded, merge initiative notes and due dates from action plan
    // with the base initiatives from the initiatives store
    if (actionPlan?.initiatives && initiatives.length > 0) {
      const newLocalValues: Record<string, { note?: string; dueDate?: string }> = {};

      actionPlan.initiatives.forEach((actionPlanInitiative) => {
        // Store values in local state for UI preservation
        newLocalValues[actionPlanInitiative.id] = {
          note: actionPlanInitiative.initiativeNote || undefined,
          dueDate: actionPlanInitiative.initiativeDueDate || undefined,
        };

        modifyInitiative(actionPlanInitiative.id, {
          initiativeDueDate: actionPlanInitiative.initiativeDueDate || null,
          initiativeNote: actionPlanInitiative.initiativeNote || null,
        });
      });

      // Update local values state first
      setLocalInitiativeValues((prev) => ({ ...prev, ...newLocalValues }));
    }
  }, [actionPlan?.initiatives, initiatives.length]);

  // Calculate initial selected items based on action plan data
  useEffect(() => {
    // console.log('selectedItems from hook:', selectedItems); // Removed for production
  }, [calculateInitialSelectedItems, selectedItems]);

  // Additional effect to monitor selectedItems changes and verify auto-selection worked
  useEffect(() => {
    // Removed debug console.log statement before production merge.
  }, [selectedItems]);

  const getMergedInitiatives = (): Initiative[] => {
    if (!initiatives.length) return [];

    // Always prioritize the current store values over action plan data
    // The store values are the "working copy" that includes user edits
    if (actionPlan?.initiatives) {
      return initiatives.map((initiative) => {
        const actionPlanData = actionPlan.initiatives.find((ap) => ap.id === initiative.id);
        return {
          ...initiative,
          // Use store values if they exist, otherwise fall back to action plan data
          initiativeDueDate:
            initiative.initiativeDueDate !== undefined
              ? initiative.initiativeDueDate
              : actionPlanData?.initiativeDueDate || null,
          initiativeNote:
            initiative.initiativeNote !== undefined
              ? initiative.initiativeNote
              : actionPlanData?.initiativeNote || null,
        };
      });
    }

    // For new action plans, return initiatives as they are in the store
    return initiatives;
  };

  const planTabs = useMemo(() => {
    const tabs = [];
    actionPlan?.guestPlan?.kpi.length !== 0 && tabs.push(PLAN_TYPE.guestPlan.id);
    actionPlan?.teamPlan?.kpi.length !== 0 && tabs.push(PLAN_TYPE.teamPlan.id);
    actionPlan?.higherTicketsPlan?.kpi.length !== 0 && tabs.push(PLAN_TYPE.higherTicketsPlan.id);

    return tabs;
  }, [actionPlan]);

  const updatePlan = async () => {
    if (isSaving) {
      console.log('Already saving, skipping duplicate save');
      return;
    }

    setIsSaving(true);

    try {
      // Small delay to allow any pending state updates to complete
      await new Promise((resolve) => setTimeout(resolve, 50));

      const preparedInitiatives = isCompleted
        ? actionPlan.initiatives.map((initiative) => {
            return {
              ...initiative,
              crIsIncluded: initiativesStatusApi.getActionedItemValueById(initiative.id) ?? false,
            };
          })
        : Object.values(selectedItems)
            .flat()
            .filter((item) => {
              return (
                !customResourcesApi.customResources.find(
                  (customItem) => customItem.crUuid === item,
                ) &&
                !item.includes(LOCAL_RESOURCE_ID_PREFIX) &&
                initiatives.find((initiative) => initiative.id === item)
              );
            })
            .map((id) => {
              // Read from localInitiativeValues first, then fall back to store
              const localValues = localInitiativeValues[id];
              const storeInitiative = initiatives.find((item) => item.id === id);

              return {
                id: id,
                initiativeNote: localValues?.note || storeInitiative?.initiativeNote || null,
                initiativeDueDate:
                  localValues?.dueDate || storeInitiative?.initiativeDueDate || null,
                crIsIncluded: storeInitiative?.crIsIncluded || false,
              };
            });

      const guestPlanStatusUpdateObject = initiativesStatusApi.getStatusObjectByPlanTypeId(
        PLAN_TYPE.guestPlan.id,
      );
      const teamPlanStatusUpdateObject = initiativesStatusApi.getStatusObjectByPlanTypeId(
        PLAN_TYPE.teamPlan.id,
      );
      const highPlanStatusUpdateObject = initiativesStatusApi.getStatusObjectByPlanTypeId(
        PLAN_TYPE.higherTicketsPlan.id,
      );

      const payload: SalonActionPlan = {
        salonDetailPage: salonId,
        salonKpi: actionPlan?.salonKpi,
        year: actionPlan?.year,
        quarter: actionPlan?.quarter,
        guestPlan: {
          kpi: Object.keys(actionPlan?.guestPlan.kpi),
          plannedGuestCount: {
            m1: (fieldsValues.plannedGuestCountm1 as number) || null,
            m2: (fieldsValues.plannedGuestCountm2 as number) || null,
            m3: (fieldsValues.plannedGuestCountm3 as number) || null,
            q:
              fieldsValues.plannedGuestCountq === NOT_PROVIDED
                ? null
                : (fieldsValues.plannedGuestCountq as number) || null,
          },
          statusUpdate: guestPlanStatusUpdateObject.statusUpdate,
          statusUpdateDate:
            guestPlanStatusUpdateObject.statusUpdateDate || guestPlanStatusUpdateObject.statusUpdate
              ? dayjs().format('YYYY-MM-DD')
              : null,
        },
        teamPlan: {
          kpi: Object.keys(actionPlan?.teamPlan.kpi),
          plannedSpCount: {
            m1: (fieldsValues.plannedSpCountm1 as number) || null,
            m2: (fieldsValues.plannedSpCountm2 as number) || null,
            m3: (fieldsValues.plannedSpCountm3 as number) || null,
            q:
              fieldsValues.plannedSpCountq === NOT_PROVIDED
                ? null
                : (fieldsValues.plannedSpCountq as number) || null,
          },
          statusUpdate: teamPlanStatusUpdateObject.statusUpdate,
          statusUpdateDate:
            teamPlanStatusUpdateObject.statusUpdateDate || teamPlanStatusUpdateObject.statusUpdate
              ? dayjs().format('YYYY-MM-DD')
              : null,
        },
        higherTicketsPlan: {
          kpi: Object.keys(actionPlan?.higherTicketsPlan.kpi),
          plannedHigherTicketsCount: {
            m1: (fieldsValues.plannedHigherTicketsCountm1 as number) || null,
            m2: (fieldsValues.plannedHigherTicketsCountm2 as number) || null,
            m3: (fieldsValues.plannedHigherTicketsCountm3 as number) || null,
            q:
              fieldsValues.plannedHigherTicketsCountq === NOT_PROVIDED
                ? null
                : (fieldsValues.plannedHigherTicketsCountq as number) || null,
          },
          plannedRetailTicketsCount: {
            m1: (fieldsValues.plannedRetailTicketsCountm1 as number) || null,
            m2: (fieldsValues.plannedRetailTicketsCountm2 as number) || null,
            m3: (fieldsValues.plannedRetailTicketsCountm3 as number) || null,
            q:
              fieldsValues.plannedRetailTicketsCountq === NOT_PROVIDED
                ? null
                : (fieldsValues.plannedRetailTicketsCountq as number) || null,
          },
          statusUpdate: highPlanStatusUpdateObject.statusUpdate,
          statusUpdateDate:
            highPlanStatusUpdateObject.statusUpdateDate || highPlanStatusUpdateObject.statusUpdate
              ? dayjs().format('YYYY-MM-DD')
              : null,
        },
        initiatives: preparedInitiatives,
        customResources: customResourcesApi.prepareCustomResourcesPayload({
          getActionedItemValueById: isCompleted
            ? initiativesStatusApi.getActionedItemValueById
            : undefined,
          storedCustomResources: actionPlan.customResources,
          selectedItems: selectedItems,
        }),
      };

      if (plannedKpiValues) {
        await updateSalonKpiPlannedData({
          salonId,
          year: actionPlan?.year,
          quarter: actionPlan?.quarter,
          showMessage,
          navigate,
          kpis: plannedKpiValues,
        });
      }

      await updateActionPlan(payload, planId, showMessage, navigate);
    } catch (error) {
      console.error('Error updating plan:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const nextBtnAction = async () => {
    try {
      await updatePlan();

      if (currentTab < planTabs.length - 1) {
        setCurrentTab(currentTab + 1);
      } else {
        setIsPreviewMode(true);
        setCurrentTab(0);
      }
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const handleSendButton = async () => {
    try {
      await updatePlan();
      setIsSendModalOpen(true);
    } catch (error) {
      console.error('Error saving:', error);
      // Still open modal even if save fails
      setIsSendModalOpen(true);
    }
  };

  const [currentTab, setCurrentTab] = useState(0);

  const rightButtons: IControlButton[] = [
    { label: t('back'), action: backButtonHandler },
    {
      label: !isPreviewMode ? (
        t('next')
      ) : (
        <>
          <SendIcon /> {t('saveSend')}
        </>
      ),
      action: () => (!isPreviewMode ? nextBtnAction() : handleSendButton()),
      variant: 'contained',
      disabled: !isMinSelected(planTabs[currentTab]) && !isPreviewMode,
      dataTestId: 'save-and-send',
    },
  ];

  const tabProps = (name: keyof typeof PLAN_TYPE) => ({
    label: PLAN_TYPE[name].title,
    id: `tab-${name}`,
    'aria-controls': `tabpanel-${name}`,
  });

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const salonDataTextPrepare = (data: KpiItemValues) => {
    if (typeof data.value === 'string') {
      return (SalonKpiValues as { [key: string]: string })[data.value];
    }
    if (data.value === null || !data.value) return NOT_PROVIDED;

    return `${data.prefix ? data.prefix + ' ' : ''}${data.value}${data.suffix || ''}`;
  };

  const kpiTextPrepare = (value: BenchmarkItemValues | string) => {
    if (typeof value === 'string') {
      return (KpiValues as { [key: string]: string })[value];
    }

    return value.minValue
      ? `${value.prefix ? value.prefix + ' ' : ''}${value.minValue}${value.suffix || ''}`
      : NOT_PROVIDED;
  };

  const rowsPrepare = ({
    rows,
    plannedKpis,
  }: {
    rows: Record<string, string | ActionGuestPlan>[];
    plannedKpis?: SalonKpiPlannedData['kpi'];
  }) => {
    return Object.entries(rows).map((row) => {
      const planData = row[1] as unknown as ActionKPI;
      const kpi = row[1].kpi as unknown as KpiItemValues;
      const plannedKpi = plannedKpis ? plannedKpis[row[0]] : undefined;
      const status = plannedKpi ? plannedKpi?.status : kpi?.status;
      const kpiValue = plannedKpis ? plannedKpi : (kpi as unknown as KpiItemValues);

      return {
        kpi: KpiTitle[row[0] as keyof typeof KpiTitle],
        avedaSalonData: kpiValue ? salonDataTextPrepare(kpiValue) : NOT_PROVIDED,
        globalBenchmarkKpi: kpiTextPrepare(
          planData.globalBenchmark as unknown as BenchmarkItemValues,
        ),
        status: (
          <Stack>
            {status && <KpiBadge result={status} />}
            <Typography>{KpiStatus[status as keyof typeof KpiStatus] || NOT_PROVIDED}</Typography>
          </Stack>
        ),
      };
    });
  };

  const rowInitiative = (rows: Initiative[]) => {
    const filteredTabRows =
      rows?.filter((row) => {
        return row.actionPlanType === convertToSnakeCase(planTabs[currentTab]);
      }) || [];
    return filteredTabRows.map((row) => {
      //if (row.groupRow === 'true') return row;

      const { id, initiativeType, priority, initiativeDueDate, title, initiativeHyperlink } = row;
      // Checkbox state is based only on manual selection, not existing data
      const isSelected = selectedItems[planTabs[currentTab]]?.includes(id) || false;
      const isGroupRow = false; //groupRow === 'true';

      return {
        ...row,
        title: initiativeHyperlink ? (
          <StyledLink href={initiativeHyperlink} target="_blank">
            {title}
          </StyledLink>
        ) : (
          <Typography>{title}</Typography>
        ),
        initiativeType: isGroupRow ? (
          <StyledTypeCell fontWeight={initiativeDueDate === 'false' ? 'bold' : 'normal'}>
            {InitiativeType[initiativeType as unknown as keyof typeof InitiativeType]}
          </StyledTypeCell>
        ) : (
          <Typography>
            {InitiativeType[initiativeType as unknown as keyof typeof InitiativeType]}
          </Typography>
        ),
        priority: (
          <Stack>
            <Typography textTransform="capitalize">{priority}</Typography>
          </Stack>
        ),
        initiativeNote: !isGroupRow && (
          <StyledNoticeStack spacing={2}>
            {editableNoteId !== id && (
              <StyledNotes
                title={localInitiativeValues[id]?.note || row.initiativeNote}
                onClick={() => !isPreviewMode && isSelected && setEditableNoteId(id)}
                style={{
                  opacity: isSelected ? 1 : 0.5,
                  cursor: isSelected && !isPreviewMode ? 'pointer' : 'default',
                  backgroundColor: isSelected ? 'transparent' : 'transparent', // Remove yellow background for auto-selected items
                }}
              >
                {localInitiativeValues[id]?.note || row.initiativeNote || `${t('notes')}...`}
              </StyledNotes>
            )}
            {editableNoteId === id && isSelected && (
              <TextField
                defaultValue={localInitiativeValues[id]?.note || row.initiativeNote || ''}
                onBlur={async (e) => {
                  const newValue = (e.target as HTMLInputElement).value;

                  // Update local state immediately
                  setLocalInitiativeValues((prev) => ({
                    ...prev,
                    [id]: {
                      ...prev[id],
                      note: newValue,
                    },
                  }));

                  // Update store asynchronously
                  try {
                    await modifyInitiative(id, { initiativeNote: newValue });
                  } catch (error) {
                    console.error('Error saving note to store:', error);
                  }

                  setEditableNoteId('');
                }}
                id={`notes_${id}`}
                variant="standard"
                autoFocus
              />
            )}
            {!isPreviewMode && (
              <Checkbox
                checked={!!isSelected}
                onChange={() => {
                  // Allow both selection and deselection regardless of existing data
                  if (!isSelected) {
                    // Check if we can add more items
                    isMaxSelected(planTabs[currentTab])
                      ? showMessage(t('notifications.maxInitiatives'), 'success')
                      : onCheckboxClick(id, planTabs[currentTab]);
                  } else {
                    // Allow deselection but don't clear local values (preserve in UI)
                    onCheckboxClick(id, planTabs[currentTab]);
                    // Only clear store values, keep local values for UI display
                    modifyInitiative(id, {
                      initiativeNote: null,
                      initiativeDueDate: null,
                    });
                  }
                }}
                id={id}
              />
            )}
          </StyledNoticeStack>
        ),
        initiativeDueDate: !isGroupRow && (
          <div
            style={{
              backgroundColor: 'transparent', // Remove yellow background
              padding: '0',
              borderRadius: '4px',
            }}
          >
            <DueDatePicker
              date={
                localInitiativeValues[id]?.dueDate
                  ? dayjs(localInitiativeValues[id]?.dueDate)
                  : initiativeDueDate
                    ? dayjs(initiativeDueDate)
                    : null
              }
              disabled={isPreviewMode || !isSelected}
              setDate={async (date) => {
                const dateString = date.format('YYYY-MM-DD');

                // Update local state immediately
                setLocalInitiativeValues((prev) => ({
                  ...prev,
                  [id]: {
                    ...prev[id],
                    dueDate: dateString,
                  },
                }));

                // Update store asynchronously
                try {
                  await modifyInitiative(id, { initiativeDueDate: dateString });
                } catch (error) {
                  console.error('Error saving date to store:', error);
                }
              }}
            />
          </div>
        ),
        actioned: (
          <Checkbox
            checked={initiativesStatusApi.getActionedItemValueById(row.id)}
            onChange={() => {
              initiativesStatusApi.updateActionedItemById(row.id);
            }}
            id={id}
          />
        ),
      };
    });
  };

  const prepareInputKpiRows = ({
    actionPlanDataRows,
    plannedKpis,
  }: {
    actionPlanDataRows: Record<string, string | ActionGuestPlan>[];
    plannedKpis: null | SalonKpiPlannedData['kpi'];
  }) => {
    return Object.entries(actionPlanDataRows).map((row) => {
      const planData = row[1] as unknown as ActionKPI;
      const kpiTitle = KpiTitle[row[0] as keyof typeof KpiTitle];
      const plannedKpi = plannedKpis && plannedKpis[row[0]];

      const kpiInput = (
        <TextField
          placeholder={quarterLabelPrepare(false)}
          onChange={(event) => {
            const numericValue = event.target.value.replace(/\D/g, '');
            if (plannedKpi) {
              setPlannedKpiValues((prev) => ({
                ...prev,
                [row[0]]: { ...plannedKpi, value: numericValue },
              }));
            }
          }}
          InputProps={{
            startAdornment: plannedKpi?.prefix ? (
              <InputAdornment position="start">{plannedKpi.prefix}</InputAdornment>
            ) : undefined,
            endAdornment: plannedKpi?.suffix ? (
              <InputAdornment position="end">{plannedKpi.suffix}</InputAdornment>
            ) : undefined,
          }}
          id={kpiTitle}
          variant="standard"
          value={plannedKpi?.value || ''}
        />
      );

      const createKpiSelectInput = (options: Record<string, string>) => {
        return (
          <FormControl variant="standard" fullWidth>
            <Select
              id={kpiTitle}
              value={plannedKpi?.value || ''}
              onChange={(event) => {
                const selectedValue = event.target.value;
                if (plannedKpi) {
                  setPlannedKpiValues((prev) => ({
                    ...prev,
                    [row[0]]: { ...plannedKpi, value: selectedValue },
                  }));
                }
              }}
            >
              {Object.entries(options).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      };

      return {
        kpi: kpiTitle,
        globalBenchmarkKpi: kpiTextPrepare(
          planData.globalBenchmark as unknown as BenchmarkItemValues,
        ),
        avedaSalonData: SELECT_INPUT_KPIS[row[0]]
          ? createKpiSelectInput(SELECT_INPUT_KPIS[row[0]])
          : kpiInput,
      };
    });
  };

  const getRowsForPreviewMode = (mergedInitiatives: Initiative[]): Initiative[] => {
    const selectedRows = mergedInitiatives.filter((row) =>
      selectedItems[planTabs[currentTab]]?.includes(row.id),
    );
    const titleRowsIds = [...new Set(selectedRows.map((item) => item.resource))];
    const selectedRowsWithTitlesIds = selectedItems[planTabs[currentTab]]
      ? [...selectedItems[planTabs[currentTab]], ...titleRowsIds]
      : [];

    return mergedInitiatives.filter((row) => selectedRowsWithTitlesIds.includes(row.id));
  };

  const quarterLabelPrepare = (isActualPlan: boolean) => {
    if (!actionPlan) return '';

    const { year, quarter } = normalizeYearQuarter({
      year: actionPlan.year,
      quarter: actionPlan.quarter,
      shouldShift: !isActualPlan,
    });

    return `CY ${year} Q${quarter}`;
  };

  const quarterFieldPrepare = (
    fields: ActionPlanCount,
    fieldsTitle: string,
    isActualPlan: boolean,
  ) => {
    const quarterFieldId = fieldsTitle + QUARTER_SUFFIX;
    const value = fieldsValues[quarterFieldId] || fields.q;

    const quarterField = {
      label: isActualPlan && !value ? t('notProvided') : quarterLabelPrepare(isActualPlan),
      id: quarterFieldId,
      name: QUARTER_SUFFIX,
      value: value,
      placeholder: ActionPlanGroupsTitle[fieldsTitle as keyof typeof ActionPlanGroupsTitle],
      disabled: isActualPlan,
      cellSize: 12,
      helperText: '',
    };

    return [quarterField];
  };

  const complitedFieldsPrepare = (values: ActionPlanCount, groupName: string) => {
    return [{ [groupName + QUARTER_SUFFIX]: values.q || NOT_PROVIDED }];
  };

  const getComplitedColumns = (groupName: string, isActualPlan: boolean) => {
    const complitedColumns = [
      {
        id: groupName + QUARTER_SUFFIX,
        name: quarterLabelPrepare(isActualPlan),
      },
    ];

    return complitedColumns;
  };

  const previewRowsPrepare = (fieldsValues: IFieldValues, groupName: string) => {
    const fieldName = groupName + QUARTER_SUFFIX;

    return [{ ...fieldsValues, ...{ [fieldName]: fieldsValues[fieldName] || NOT_PROVIDED } }];
  };

  return (
    <>
      <ControlBar
        backButtonAction={async () => {
          try {
            // Only save if we have data to save
            if (
              initiatives.length > 0 &&
              Object.keys(selectedItems).some((key) => selectedItems[key].length > 0)
            ) {
              await updatePlan();
            }
            navigate(generatePath(URLs.Salon, { salonId }));
          } catch (error) {
            console.error('Error saving draft:', error);
            // Still navigate back even if save fails
            navigate(generatePath(URLs.Salon, { salonId }));
          }
        }}
        title={<Typography variant="h5">{actionPlan?.title || t('newPlan')}</Typography>}
        withKpiButton
      />
      <Loader isLoading={isLoading || isSalonDataLoading} />
      <Container>
        <Tabs
          textColor="secondary"
          indicatorColor="secondary"
          value={currentTab}
          onChange={handleTabChange}
          aria-label="Plans"
        >
          {planTabs.map((plan, index) => (
            <Tab key={`index_${index}`} {...tabProps(plan as keyof typeof PLAN_TYPE)} />
          ))}
        </Tabs>
      </Container>
      <Divider />
      {planTabs.map((plan, index) => (
        <TabPanel key={`index_${index}`} value={currentTab} index={index}>
          {isPreviewMode && (
            <PreviewTitleBox>
              <Typography variant="h4" textTransform="capitalize">
                {actionPlan?.title}
              </Typography>
            </PreviewTitleBox>
          )}
          {actionPlan &&
            Object.entries(actionPlan?.[plan as keyof SalonActionPlan])
              .sort((a, b) => blocksOrder[a[0]] - blocksOrder[b[0]])
              .map((item, index) => {
                const isActualField =
                  item[0].includes('actual') || item[0].includes('Transactions');

                if (item[0].includes('statusUpdate')) return null;

                return item[0].includes('kpi') ? (
                  <>
                    <Box mt={6} gap={6}>
                      <Typography variant="h6" mb={2}>
                        {
                          ActionPlanGroupsTitle[
                            `${item[0]}Actual` as keyof typeof ActionPlanGroupsTitle
                          ]
                        }
                      </Typography>
                      <TableComponent
                        rows={rowsPrepare({ rows: item[1] })}
                        columns={LocalizedColumns(CalculatedKpiColumns, t)}
                        dataTestId="selected-kpi-table"
                      />
                    </Box>
                    <Box mt={6} gap={6}>
                      <Typography variant="h6" mb={2}>
                        {
                          ActionPlanGroupsTitle[
                            `${item[0]}Planned` as keyof typeof ActionPlanGroupsTitle
                          ]
                        }
                      </Typography>
                      <TableComponent
                        rows={
                          isPreviewMode
                            ? rowsPrepare({
                                rows: item[1],
                                plannedKpis: plannedKpiValues,
                              })
                            : prepareInputKpiRows({
                                actionPlanDataRows: item[1],
                                plannedKpis: plannedKpiValues,
                              })
                        }
                        columns={LocalizedColumns(
                          isPreviewMode ? CalculatedKpiColumns : RawKpiColumns,
                          t,
                        )}
                        dataTestId="planned-kpi-table"
                      />
                    </Box>
                  </>
                ) : !isPreviewMode ? (
                  <FormGrid
                    onChangeFieldValues={(values) =>
                      setFieldsValues((prevState) => ({ ...prevState, ...values }))
                    }
                    key={item[0] + index}
                    formDescription={
                      ActionPlanGroupsTitleDescription[
                        item[0] as keyof typeof ActionPlanGroupsTitleDescription
                      ]
                    }
                    formName={ActionPlanGroupsTitle[item[0] as keyof typeof ActionPlanGroupsTitle]}
                    fields={quarterFieldPrepare(item[1], item[0], isActualField)}
                  />
                ) : (
                  <Box key={item[0] + index} mt={6} gap={6}>
                    <Typography variant="h6" mb={2}>
                      <FormItemLabel
                        label={ActionPlanGroupsTitle[item[0] as keyof typeof ActionPlanGroupsTitle]}
                        description={false}
                      />
                    </Typography>
                    <TableComponent
                      rows={
                        isCompleted
                          ? complitedFieldsPrepare(item[1], item[0])
                          : previewRowsPrepare(fieldsValues, item[0])
                      }
                      columns={getComplitedColumns(item[0], isActualField)}
                      dataTestId={`preview-${item[0].toLowerCase().replaceAll(' ', '-')}-table`}
                    />
                  </Box>
                );
              })}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box mt={6}>
              <Typography variant="h6">{t('initiatives')}</Typography>
              <Typography mb={2}>{t('selectInitiative')}</Typography>
              <TableComponent
                rows={rowInitiative(
                  isPreviewMode
                    ? isCompleted
                      ? actionPlan?.initiatives
                      : getRowsForPreviewMode(getMergedInitiatives())
                    : getMergedInitiatives(),
                )}
                columns={LocalizedColumns(
                  isCompleted ? CompletedInitiativeColumns : InitiativeColumns,
                  t,
                )}
                dataTestId="initiatives-table"
                isCompleted={isCompleted}
                isPreviewMode={isPreviewMode}
                customResourcesData={{
                  customResourcesApi: customResourcesApi,
                  planTypeId: planTabs[currentTab] as keyof typeof PLAN_TYPE,
                  onCheckboxClick: onCheckboxClick,
                  selectedItems: selectedItems,
                }}
                initiativesStatusData={{
                  initiativesStatusApi: initiativesStatusApi,
                }}
              />
            </Box>
          </LocalizationProvider>
        </TabPanel>
      ))}
      <SendFormModal
        plans={planTabs}
        open={isSendModalOpen}
        handleClose={() => {
          setIsSendModalOpen(false);
        }}
      />
      <ControlBar rightButtons={rightButtons} />
    </>
  );
};
